import React, { useState } from 'react';
import Textarea from '../../../components/ui/Textarea';
import { useToast } from '../../../components/ui/Toast';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import notificationService from '../../../services/notificationService';

const TemplateManager = ({ conferenceId, templates, onUpdate }) => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    notificationType: 'custom',
    subject: '',
    message: ''
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template?.name,
      notificationType: template?.notificationType,
      subject: template?.subject,
      message: template?.message
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      notificationType: 'custom',
      subject: '',
      message: ''
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (!formData?.subject?.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      if (editingTemplate) {
        await notificationService?.updateTemplate(editingTemplate?.id, formData);
        toastSuccess('Template updated successfully');
      } else {
        await notificationService?.createTemplate({
          ...formData,
          conferenceId
        });
        toastSuccess('Template created successfully');
      }
      handleCancel();
      onUpdate?.();
    } catch (err) {
      toastError(err?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await notificationService?.deleteTemplate(templateId);
      toastSuccess('Template deleted successfully');
      onUpdate?.();
    } catch (err) {
      toastError(err?.message || 'Failed to delete template');
    }
  };

  const notificationTypeOptions = [
    { value: 'assignment', label: 'Reviewer Assignment' },
    { value: 'decision', label: 'Paper Decision' },
    { value: 'status_update', label: 'Status Update' },
    { value: 'deadline_reminder', label: 'Deadline Reminder' },
    { value: 'custom', label: 'Custom Message' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Notification Templates</h2>
        {!showForm && (
          <Button onClick={() => setShowForm(true)}>
            <Icon name="Plus" size={16} className="mr-2" />
            New Template
          </Button>
        )}
      </div>

      {/* Template Form */}
      {showForm && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {editingTemplate ? 'Edit Template' : 'Create Template'}
          </h3>

          <div className="space-y-4">
            <Input
              label="Template Name"
              type="text"
              placeholder="e.g., Reviewer Assignment Template"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            <div>
              <Textarea
                label="Message"
                required
                value={formData?.message || ''}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
                placeholder="Enter template message…"
                rows={6}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {errors?.message && (
                <p className="text-xs text-destructive mt-1">{errors?.message}</p>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
              <Button variant="outline" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={16} className="mr-2" />
                    Save Template
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Template List */}
      {!showForm && (
        <div className="space-y-4">
          {templates?.length === 0 ? (
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Templates Yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create reusable notification templates to save time
              </p>
              <Button onClick={() => setShowForm(true)}>
                <Icon name="Plus" size={16} className="mr-2" />
                Create First Template
              </Button>
            </div>
          ) : (
            templates?.map(template => (
              <div
                key={template?.id}
                className="bg-card rounded-lg border border-border p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-lg font-semibold text-foreground">{template?.name}</h3>
                      {template?.isDefault && (
                        <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground capitalize">
                      {template?.notificationType?.replace('_', ' ')}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(template)}
                    >
                      <Icon name="Edit" size={14} className="mr-1" />
                      Edit
                    </Button>
                    {!template?.isDefault && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(template?.id)}
                      >
                        <Icon name="Trash2" size={14} />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <span className="text-xs font-medium text-muted-foreground">Subject:</span>
                    <p className="text-sm text-foreground mt-1">{template?.subject}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground">Message:</span>
                    <p className="text-sm text-foreground mt-1 line-clamp-3">{template?.message}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateManager;